import React, {useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {BottomSheetModal} from '@homeaway/react-modal';
import Tooltip from '@homeaway/react-tooltip';

import './VrboPriceSummary.less';

function VrboPriceSummary(props) {
    const [expanded, setExpanded] = useState(false);
    const {
        perNight,
        total,
        mainPrice,
        priceAlign,
        discountBadge,
        totalFallbackMessage,
        isDesktop,
        strikeThroughPrice
    } = props;
    const handleClick = () => {
        setExpanded((prevExpanded) => !prevExpanded);
    };

    const renderTooltip = (tooltip, amount) => {
        return (
            <BottomSheetModal
                isOpen={expanded}
                onClose={handleClick}
                id={`price-${amount}`}
            >
                <div className="VrboPriceSummary__tooltip">{tooltip}</div>
            </BottomSheetModal>
        );
    };

    const renderDiscountBadge = () => {
        if (priceAlign === 'inline' || !discountBadge || !total) {
            return null;
        }

        return (
            <span className="VrboPriceSummary__line VrboPriceSummary__discount-badge">
                {discountBadge}
            </span>
        );
    };

    const renderStrikeThroughPrice = () => {
        if (!strikeThroughPrice) {
            return null;
        }
        const {
            roundedFormattedAmount,
            formattedAmount,
            content
        } = strikeThroughPrice;
        const className = classNames('', {
            VrboPriceSummary__strike: true,
            VrboPriceSummary__amount: true
        });
        if (!isDesktop) {
            return (
                <span key="total" className="VrboPriceSummary__line">
                    {content ? (
                        renderTooltip(content, roundedFormattedAmount)
                    ) : null}
                    <span
                        id="tooltip-button"
                        role="button"
                        tabIndex={0}
                        className={className}
                        onClick={handleClick}
                    >
                        {roundedFormattedAmount || formattedAmount}
                    </span>
                </span>
            );
        }
        return (
            <Tooltip
                placement={'bottom'}
                id={`price-${roundedFormattedAmount}`}
                tooltipType={'popover'}
                content={content}
                trigger={'click'}
                wrapperClassName={'VrboPriceSummary__line'}
                wrapperStyle={{display: 'flex'}}
            >
                <span className={className}>
                    {roundedFormattedAmount || formattedAmount}
                </span>
            </Tooltip>
        );
    };

    const renderTotal = () => {
        const isPrimary = total && mainPrice === 'total';
        if (!total) {
            return (
                <span key="total" className="VrboPriceSummary__line">
                    <span className="VrboPriceSummary__message">
                        {totalFallbackMessage}
                    </span>
                </span>
            );
        }

        const {roundedFormattedAmount, formattedAmount} = total;
        const containerClass = classNames('', {
            'VrboPriceSummary__line--primary': isPrimary,
            VrboPriceSummary__line: !isPrimary
        });
        const className = classNames('', {
            'VrboPriceSummary__amount--primary': isPrimary,
            VrboPriceSummary__amount: !isPrimary
        });

        return (
            <span key="total" className={containerClass}>
                {isPrimary && renderStrikeThroughPrice()}
                <span className={className}>
                    {roundedFormattedAmount || formattedAmount}
                </span>
            </span>
        );
    };

    const renderNightly = () => {
        if (!perNight) {
            return null;
        }

        const {
            roundedFormattedAmount,
            formattedAmount,
            pricePeriodDescription
        } = perNight;
        const isPrimary = mainPrice === 'perNight';

        const containerClass = classNames('', {
            'VrboPriceSummary__line--primary': isPrimary,
            'VrboPriceSummary__line--inline': priceAlign === 'inline',
            VrboPriceSummary__line: !isPrimary && priceAlign !== 'inline'
        });
        const className = classNames('VrboPriceSummary__amount--nightly', {
            'VrboPriceSummary__amount--primary': isPrimary,
            VrboPriceSummary__amount: !isPrimary
        });
        return (
            <span key="per_night" className={containerClass}>
                {isPrimary && renderStrikeThroughPrice()}
                <span className={className}>
                    {roundedFormattedAmount || formattedAmount}
                </span>
                <span className="VrboPriceSummary__period">
                    {pricePeriodDescription}
                </span>
            </span>
        );
    };

    const totalPrice = renderTotal();
    const nightlyPrice = renderNightly();
    const prices =
        mainPrice === 'total'
            ? [totalPrice, nightlyPrice]
            : [nightlyPrice, totalPrice];

    const containerClass = classNames('VrboPriceSummary', {
        'VrboPriceSummary--left':
            priceAlign === 'left' || (priceAlign === 'inline' && !total),
        'VrboPriceSummary--right': priceAlign === 'right',
        'VrboPriceSummary--inline': priceAlign === 'inline' && total
    });
    return (
        <span className={containerClass}>
            {renderDiscountBadge()}
            {prices}
        </span>
    );
}

VrboPriceSummary.propTypes = {
    perNight: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        formattedAmount: PropTypes.string.isRequired,
        roundedFormattedAmount: PropTypes.string,
        pricePeriodDescription: PropTypes.string.isRequired
    }),
    total: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        formattedAmount: PropTypes.string.isRequired,
        roundedFormattedAmount: PropTypes.string
    }),
    strikeThroughPrice: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        formattedAmount: PropTypes.string.isRequired,
        roundedFormattedAmount: PropTypes.string,
        content: PropTypes.string
    }),
    label: PropTypes.string,
    mainPrice: PropTypes.oneOf(['total', 'perNight']).isRequired,
    priceAlign: PropTypes.oneOf(['left', 'right', 'inline']).isRequired,
    discountBadge: PropTypes.string,
    totalFallbackMessage: PropTypes.string.isRequired,
    isDesktop: PropTypes.bool.isRequired
};

VrboPriceSummary.defaultProps = {
    mainPrice: 'total',
    priceAlign: 'right',
    discountBadge: null,
    totalFallbackMessage: 'View details for total price',
    total: null,
    perNight: null,
    isDesktop: true,
    strikeThroughPrice: null
};

export default VrboPriceSummary;
